/**
 * Card brand detection + Luhn check + per-brand CVV/length rules.
 *
 * Adaptado de stakeverse/src/stores/payment-methods-store.ts. Pure regex,
 * sem dependência externa. Inclui detecção robusta de Elo + Hipercard
 * (BR-specific) que faltava no projeto antes.
 *
 * Por que existe: o `CreditCardForm` antigo deixava o Asaas.js detectar
 * a bandeira só após tokenização — usuário não via feedback nenhum
 * enquanto digitava, e cartões válidos podiam falhar silenciosamente
 * (ex: Visa) por falta de validação client-side antes do submit.
 */

export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'elo'
  | 'hipercard'
  | 'jcb'
  | 'diners'
  | 'discover'
  | 'unknown';

export const ALL_BRANDS: Exclude<CardBrand, 'unknown'>[] = [
  'visa',
  'mastercard',
  'amex',
  'elo',
  'hipercard',
  'jcb',
  'diners',
  'discover',
];

/**
 * Detects the brand from the first digits of a card number. Order matters:
 * brands with more specific prefixes (Elo, Hipercard) MUST come before the
 * broader ones (Visa = 4 swallows several Elo prefixes that also start with 4).
 */
export function detectBrand(pan: string): CardBrand {
  const c = pan.replace(/\D/g, '');
  if (c.length < 1) return 'unknown';

  // Amex: 34, 37
  if (/^3[47]/.test(c)) return 'amex';
  // Diners Club: 300–305, 36, 38, 39
  if (/^3(0[0-5]|[689])/.test(c)) return 'diners';
  // JCB: officially 3528–3589, but nothing else uses 35 prefix in BR
  if (/^35/.test(c)) return 'jcb';
  // Discover: 6011, 644–649, 65, 6221–6229
  if (/^(6011|64[4-9]|65|622[1-9])/.test(c)) return 'discover';
  // Elo (Brazilian) — long prefix list, must come BEFORE Visa
  if (
    /^(4011(78|79)|431274|438935|451416|457393|457631|457632|504175|506(699|7[0-6]|77[0-8])|509[0-9]{3}|627780|636297|636368|65003[1-3]|65003[5-9]|65004[0-9]|65005[01]|65040[5-9]|65041[0-9]|65042[0-9]|6504[3-9]|6505[0-2][0-9]|65053[0-8]|65054[1-9]|6505[5-8]|650590|650901[0-9]|65091[4-9]|6509[2-7]|65098[0-9]|6509[9]|651652[0-9]|65165[3-9]|65166|65916[7-9]|65917[0-9])/.test(c)
  )
    return 'elo';
  // Hipercard: 606282, 3841 — must come BEFORE Mastercard/Visa (3841 conflicts)
  if (/^(606282|3841)/.test(c)) return 'hipercard';
  // Mastercard: 51-55, 2221-2720
  if (/^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d\d|27[01]\d|2720)/.test(c))
    return 'mastercard';
  // Visa: 4 (must come AFTER Elo/Hipercard 4-prefix checks)
  if (/^4/.test(c)) return 'visa';
  return 'unknown';
}

/** Luhn check — true se o número passa no checksum (algoritmo MOD-10). */
export function luhnValid(pan: string): boolean {
  const clean = pan.replace(/\D/g, '');
  if (clean.length < 13 || clean.length > 19) return false;
  let sum = 0;
  let dbl = false;
  for (let i = clean.length - 1; i >= 0; i--) {
    let d = Number(clean[i]);
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
}

/** Comprimento esperado do PAN por bandeira (sem espaços). */
export function panLength(brand: CardBrand): number {
  switch (brand) {
    case 'amex':
      return 15;
    case 'diners':
      return 14;
    case 'visa':
    case 'mastercard':
    case 'elo':
    case 'hipercard':
    case 'jcb':
    case 'discover':
    default:
      return 16;
  }
}

/** Comprimento do CVV/CVC por bandeira. Amex usa 4 ('CID'); resto usa 3. */
export function cvvLength(brand: CardBrand): number {
  return brand === 'amex' ? 4 : 3;
}

/**
 * Formata um PAN com espaços. Amex usa 4-6-5 (oficial); demais usam 4-4-4-4.
 *
 * Mantemos o spacing oficial pra que o autofill do navegador (cc-number)
 * preencha visualmente certo, e pra que o display espelhe o cartão físico.
 */
export function formatCardNumber(value: string, brand?: CardBrand): string {
  const digits = value.replace(/\D/g, '');
  const b = brand ?? detectBrand(digits);
  if (b === 'amex') {
    const sliced = digits.slice(0, 15);
    const parts = [sliced.slice(0, 4), sliced.slice(4, 10), sliced.slice(10, 15)].filter(Boolean);
    return parts.join(' ');
  }
  const max = panLength(b);
  const sliced = digits.slice(0, max);
  return sliced.match(/.{1,4}/g)?.join(' ') ?? sliced;
}

/** True quando o PAN tem comprimento e checksum válidos pra bandeira. */
export function isCardNumberValid(pan: string): boolean {
  const clean = pan.replace(/\D/g, '');
  const brand = detectBrand(clean);
  if (brand === 'unknown') return false;
  if (clean.length !== panLength(brand)) return false;
  return luhnValid(clean);
}

/** True se MM/YY está no futuro (ou no mês corrente). */
export function isExpiryValid(month: string | number, year: string | number): boolean {
  const m = typeof month === 'string' ? Number(month) : month;
  const y = typeof year === 'string' ? Number(year) : year;
  if (!Number.isFinite(m) || !Number.isFinite(y)) return false;
  if (m < 1 || m > 12) return false;
  // Aceita YY (28) ou YYYY (2028)
  const fullYear = y < 100 ? 2000 + y : y;
  const now = new Date();
  const exp = new Date(fullYear, m, 0, 23, 59, 59); // último dia do mês
  return exp >= now;
}

/** Label humanizado por bandeira. */
export function brandLabel(brand: CardBrand): string {
  const labels: Record<CardBrand, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    elo: 'Elo',
    hipercard: 'Hipercard',
    jcb: 'JCB',
    diners: 'Diners Club',
    discover: 'Discover',
    unknown: 'Cartão',
  };
  return labels[brand];
}
